/**
 * #538 — "PDF" becoming "PDApp" while a PDApp loads in its window: the splash's loading morph
 * (features/share-links/api/splash-page.ts, the PDApp intro deck's 1.9 s timing), drawn by the SPA.
 *
 * It covers the window from the moment it opens until the PDApp's page has loaded AND the morph has
 * played once (or, if the page never says it loaded, a few seconds after) — then fades out. The
 * window renders it in two places (while it is still starting, then over the viewer); `startedAt`
 * keeps the animation continuous across that swap instead of replaying it.
 */
import { useEffect, useState } from 'react'

const MORPH_MS = 1900
/** The morph plus a short hold on "PDApp", as the splash waits before its Open. */
const SHOWN_AT_LEAST_MS = 2300
/** A page that never reports its load still gets the window back. */
const GIVE_UP_MS = 6000

const CSS = `
.pdm { position: absolute; inset: 0; z-index: 25; display: flex; align-items: center; justify-content: center; transition: opacity .35s ease; }
.pdm.out { opacity: 0; pointer-events: none; }
.pdm .word { font-weight: 800; letter-spacing: -0.03em; line-height: 1; font-size: clamp(40px, 6vw, 76px); display: inline-flex; align-items: baseline; user-select: none; margin-top: -6%; }
.pdm .slot { display: inline-grid; justify-items: start; align-items: baseline; width: .60em; overflow: visible; animation: pdmSlot ${MORPH_MS}ms cubic-bezier(.5,.1,.3,1) both; animation-delay: var(--pdm-delay); }
.pdm .slot > span { grid-area: 1/1; display: inline-block; }
.pdm .f { animation: pdmF ${MORPH_MS}ms cubic-bezier(.55,.06,.68,.19) both; animation-delay: var(--pdm-delay); }
.pdm .a { background: linear-gradient(100deg,#a855f7,#3b82f6); -webkit-background-clip: text; background-clip: text; color: transparent; padding-right: .12em; margin-right: -.12em; animation: pdmA ${MORPH_MS}ms cubic-bezier(.2,.7,.3,1) both; animation-delay: var(--pdm-delay); }
@keyframes pdmSlot { 0%,72% { width: .60em; } 94%,100% { width: 2.14em; } }
@keyframes pdmF { 0%,45% { opacity: 1; transform: none; } 62%,100% { opacity: 0; transform: scale(.45); } }
@keyframes pdmA { 0%,72% { opacity: 0; transform: translateX(.5em); } 94%,100% { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .pdm .slot, .pdm .f, .pdm .a { animation: none; }
  .pdm .slot { width: 2.14em; } .pdm .f { opacity: 0; } .pdm .a { opacity: 1; }
}
`

/** Whether the morph should still cover the window: until loaded and played (or given up on). */
export function usePdappLoadMorph(startedAt: number, loaded: boolean): boolean {
  const [now, setNow] = useState(() => performance.now())
  const elapsed = now - startedAt
  const shown = !(elapsed >= SHOWN_AT_LEAST_MS && (loaded || elapsed >= GIVE_UP_MS))
  useEffect(() => {
    if (!shown) return
    const next = loaded ? SHOWN_AT_LEAST_MS : Math.min(GIVE_UP_MS, Math.max(SHOWN_AT_LEAST_MS, elapsed + 250))
    const t = window.setTimeout(() => setNow(performance.now()), Math.max(0, next - elapsed) + 10)
    return () => window.clearTimeout(t)
  }, [shown, loaded, elapsed])
  return shown
}

/** The overlay. Mount it while `usePdappLoadMorph` says so; it fades itself out on `leaving`. */
export function PdappLoadMorph({ startedAt, leaving = false }: { startedAt: number; leaving?: boolean }) {
  // A negative delay picks the animation up where it is (the window swaps what it renders).
  const delay = `${-Math.min(MORPH_MS, Math.max(0, performance.now() - startedAt))}ms`
  return (
    <div className={`pdm themed-bg-primary themed-text${leaving ? ' out' : ''}`} aria-hidden="true" style={{ ['--pdm-delay' as string]: delay }}>
      <style>{CSS}</style>
      <div className="word"><span>PD</span><span className="slot"><span className="f">F</span><span className="a">App</span></span></div>
    </div>
  )
}
